import { ClipboardEventData } from '@ckeditor/ckeditor5-clipboard/src/clipboardobserver';
import { Editor, EnterEventData } from '@ckeditor/ckeditor5-core/src/editor';
import { Emoji } from 'emoji-mart';
import _, { union } from 'lodash';
import React, { createRef, PureComponent, RefObject } from 'react';
import ReactDOM from 'react-dom';
import { ProjectId } from 'api/models';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { MentionData, OnUserMention } from 'components/molecules/mention';
import { getCkEditorUploadConfig } from 'components/organisms/ck-collab-editor/helpers';
import { cloudServices } from 'config/ck-editor';
import { TemplateObjectType } from 'data/templates/api';
import { queueMacrotask } from 'utils/macrotask';
import { CKMentionPopup } from 'vendor/ckeditor/react-components/mention';
import {
  CKVariablesPopup,
  VariablesMeta as AutomationsVariablesMeta,
} from 'vendor/ckeditor/react-components/variables';
import { Checklist } from './checklist';
import { CKEditor5 } from './ck-editor5';
import { EmojiPicker } from './emoji-picker';
import { executeAddEmojiCommand } from './execute-emoji-command';
import { InsertImage } from './insert-image';
import {
  base_plugins,
  base_variable_plugins,
  conditional_variable_plugins,
  formatting_plugins,
  image_plugins,
  link_plugins,
  mention_plugins,
  simple_variable_plugins,
  toolbar_plugins,
} from './plugins';
import styles from './rich-text-editor.module.css';
import { toolbar } from './toolbar';
import { Config } from './types';
import { Fallback, isHtmlIncluded } from './utils';

export interface RichTextEditorProps {
  data: string;
  placeholder?: string;
  className?: string;
  readonly?: boolean;
  /** This property is only respected on initialisation, and changes are not propagated. */
  autosaveInterval?: number;
  /** This property is only respected on initialisation, and changes are not propagated. */
  disallowImages?: boolean;
  disallowMentions?: boolean;
  mentionData?: MentionData;
  save?: (data: string) => Promise<void>;
  /** This property is only respected on initialisation, and changes are not propagated. */
  onInit?: () => void;
  onChange?: (data: string) => void;
  onBlur?: (data: string) => void;
  onFocus?: (data: string) => void;
  onError?: (error: Error) => void;
  onEnter?: () => void;
  onFileAdded?: (files: FileList) => boolean | void;
  onMentionAdd?: (data: any) => void;
  onUserMention?: OnUserMention;
  noToolbar?: boolean;
  noFormatting?: boolean;
  projectId?: ProjectId; // NOTE: putting back projectId to support @mentions. It is not needed after backend changes.
  autoFocus?: boolean;
  disallowNewLine?: boolean;
  suppressCloudServices?: boolean;
  noVariables?: boolean;
  allowSimpleVariables?: boolean;
  allowConditionalVariables?: boolean;
  // we will replace the below prop with object like editorProp, if we pass more than one prop to conditional varialble editor.
  disallowNewLineInConditionalVariables?: boolean;
  resolveVariablesToFallback?: boolean;
  templateType?: TemplateObjectType;
  testId?: string;
  automationsVariablesMeta?: AutomationsVariablesMeta;
  id?: string;
  noUrlFormatting?: boolean;
}

interface RichTextEditorState {
  ready: boolean;
  mentionPopupPosition: {
    top: number;
    left: number;
  };
  variablePopupPosition: {
    top: number;
    left: number;
  };
}

interface Position {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Below disableCommand API is referred from CKE docs.
 */
function disableCommand(cmd) {
  function forceDisable(evt) {
    evt.return = false;
    evt.stop();
  }
  cmd.on('set:isEnabled', forceDisable, { priority: 'highest' });
  cmd.isEnabled = false;
}

export class RichTextEditor extends PureComponent<
  RichTextEditorProps,
  RichTextEditorState
> {
  static EmojiPicker = EmojiPicker;

  static InsertImage = InsertImage;

  static Checklist = Checklist;

  mentionPopupContainer: RefObject<HTMLDivElement>;

  variablePopupContainer: RefObject<HTMLDivElement>;

  wrapperRef: RefObject<HTMLDivElement>;

  toolbarRef: RefObject<HTMLDivElement>;

  editor: Editor | null;

  config: Config;

  private onInitHandler?: () => void;

  constructor(props: RichTextEditorProps) {
    super(props);
    this.wrapperRef = createRef();
    this.toolbarRef = createRef();
    this.mentionPopupContainer = createRef();
    this.variablePopupContainer = createRef();
    this.config = {
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          ...[...Array(6).keys()] // Add heading levels 1-6, by default CKEditor enables only 1-3
            .map((i) => {
              const level = i + 1;

              return {
                model: `heading${level}`,
                view: `h${level}`,
                title: `Heading ${level}`,
                class: `ck-heading_heading${level}`,
              };
            }),
        ],
      },
      automationsVariablesMeta: props.automationsVariablesMeta,
      emoji: {
        renderer: (emoji, container) => {
          ReactDOM.render(<Emoji size={16} native emoji={emoji} />, container);
        },
      },
      plugins: union(
        base_plugins,
        props.noUrlFormatting ? [] : link_plugins,
        props.noFormatting ? [] : formatting_plugins,
        props.noToolbar ? [] : toolbar_plugins,
        props.disallowImages ? [] : image_plugins,
        props.disallowMentions ? [] : mention_plugins,
        props.noVariables
          ? []
          : props.allowConditionalVariables
          ? [...simple_variable_plugins, ...conditional_variable_plugins]
          : props.allowSimpleVariables
          ? simple_variable_plugins
          : base_variable_plugins,
      ),
      mention: {
        feeds: [
          {
            marker: '@',
          },
        ],
      },
      variables: {
        feeds: [
          {
            marker: '{',
          },
        ],
        disallowNewLineInConditionalVariables:
          props.disallowNewLineInConditionalVariables,
      },
      ...(props.suppressCloudServices ? {} : { cloudServices }),
      balloonToolbar: toolbar,
      placeholder: props.placeholder || '',
      autosave:
        props.save == null
          ? undefined
          : {
              save: this.save.bind(this),
              waitingTime: props.autosaveInterval || 1000,
            },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'http://',
      },
      ...(props.suppressCloudServices ? {} : getCkEditorUploadConfig()),
      variablesConfig: {
        allowSimpleVariables: props.allowSimpleVariables,
        resolveVariablesToFallback: props.resolveVariablesToFallback,
      },
    };
    this.state = {
      ready: false,
      mentionPopupPosition: {
        top: 0,
        left: 0,
      },
      variablePopupPosition: {
        top: 0,
        left: 0,
      },
    };
    this.editor = null;
    this.onInitHandler = props.onInit;
    this.updateMentionPopupPosition =
      this.updateMentionPopupPosition.bind(this);
    this.updateVariablePopupPosition =
      this.updateVariablePopupPosition.bind(this);
  }

  private onInit = (editor: Editor) => {
    this.editor = editor;

    this.setState({ ready: true }, () => {
      const { onInitHandler } = this;

      this.applyBalloonToolbarStyles();
      this.handleEnter();
      this.handleFiles();
      onInitHandler?.();
    });

    const { autoFocus, readonly, disallowNewLine } = this.props;

    if (autoFocus && !readonly) {
      this.focusAtEnd();
    }

    if (disallowNewLine) {
      disableCommand(editor.commands.get('enter'));
      disableCommand(editor.commands.get('shiftEnter'));
    }
  };

  private handleEnter = () => {
    const { editor } = this;

    if (editor == null) {
      return;
    }
    const { view } = editor.editing;

    view.document.on<EnterEventData>(
      'enter',
      (event, data) => {
        const { onEnter } = this.props;

        if (onEnter == null) {
          return;
        }
        event.stop();
        data.preventDefault();
        if (data.isSoft) {
          editor.execute('enter');
          view.scrollToTheSelection();
        } else {
          onEnter();
        }
      },
      { priority: 'normal' },
    );
  };

  private handleFiles = () => {
    const { editor } = this;

    if (editor == null) {
      return;
    }
    editor.editing.view.document.on<ClipboardEventData>(
      'clipboardInput',
      (event, data) => {
        if (editor.isReadOnly || data.dataTransfer.files.length === 0) {
          return;
        }

        // Skip if non empty HTML data is included.
        // https://github.com/ckeditor/ckeditor5-upload/issues/68
        if (isHtmlIncluded(data.dataTransfer)) {
          return;
        }

        const { onFileAdded } = this.props;

        if (onFileAdded != null) {
          const stop = onFileAdded(data.dataTransfer.files) || false;

          if (stop) {
            event.stop();
          }
        }
      },
      { priority: 'highest' },
    );
  };

  setData() {
    const { data } = this.props;

    this.editor?.setData(data);
  }

  private applyBalloonToolbarStyles = () => {
    if (this.editor == null) {
      return;
    }
    const { view } = this.editor.plugins.get<any>('ContextualBalloon');

    view.on('change:class', (_event: any, _name: string, value: string) => {
      if (!value.startsWith(styles.balloon)) {
        view.class = `${styles.balloon} ${value}`;
      }
    });
  };

  public addEmoji = (emoji: string) =>
    executeAddEmojiCommand(emoji, this.editor);

  public insertImages = (images: FileList) => {
    const { editor } = this;

    if (editor == null) {
      return;
    }
    editor.execute('uploadImage', { file: Array.from(images) });
    editor.editing.view.focus();
  };

  public insertChecklist = (): void => {
    const { editor } = this;

    if (editor == null) {
      return;
    }
    editor.execute('todoList');
    editor.editing.view.focus();
  };

  public focus = () => {
    const { editor } = this;

    if (editor == null) {
      return;
    }

    queueMacrotask(() => {
      editor.focus();
    });
  };

  public focusAtEnd = () => {
    const { editor } = this;

    if (editor == null) {
      return;
    }

    editor.model.change((writer) => {
      writer.setSelection(editor.model.document.getRoot(), 'end');
    });
    queueMacrotask(() => {
      editor.editing.view.focus();
    });
  };

  updateMentionPopupPosition = (position: Position): void => {
    if (position.left > 0) {
      this.setState({
        mentionPopupPosition: {
          left: position.left,
          top: position.top,
        },
      });
    }
  };

  updateVariablePopupPosition = (position: Position): void => {
    if (position.left > 0) {
      this.setState({
        variablePopupPosition: {
          left: position.left,
          top: position.top,
        },
      });
    }
  };

  private async save(editor: Editor) {
    const { save } = this.props;

    return save?.(editor.getData()) || Promise.resolve();
  }

  render() {
    const {
      data,
      className,
      readonly,
      placeholder,
      mentionData,
      onChange,
      onBlur,
      onError,
      onFocus,
      onMentionAdd,
      onUserMention,
      projectId,
      disallowMentions,
      noVariables,
      templateType,
      allowConditionalVariables,
      automationsVariablesMeta: variablesMeta,
      id,
    } = this.props;

    const { config: _config, onInit, editor } = this;
    const config = {
      ..._config,
      automationsVariablesMeta: variablesMeta,
    };

    const { ready, mentionPopupPosition, variablePopupPosition } = this.state;

    const customClassName = [
      styles.rte,
      ready ? styles.ready : '',
      readonly ? styles.readonly : '',
      className || '',
    ]
      .filter(Boolean)
      .join(' ');

    const { testId } = this.props;

    return (
      <ErrorBoundary fallback={Fallback}>
        {!readonly && ready && !disallowMentions ? (
          <CKMentionPopup
            projectId={projectId && projectId[1]}
            renderOnPortal
            editor={editor}
            attachTo={this.mentionPopupContainer.current}
            updateParentElementLocation={this.updateMentionPopupPosition}
            mentionData={mentionData}
            onMentionAdd={onMentionAdd}
            onUserMention={onUserMention}
          >
            <div
              ref={this.mentionPopupContainer}
              className={styles.mention}
              style={{
                top: Math.round(mentionPopupPosition.top),
                left: Math.round(mentionPopupPosition.left),
                position: 'fixed',
              }}
            />
          </CKMentionPopup>
        ) : null}

        {!readonly && ready && !noVariables ? (
          <CKVariablesPopup
            meta={variablesMeta}
            projectId={projectId && projectId[1]}
            editor={editor}
            attachTo={this.variablePopupContainer.current}
            updateParentElementLocation={this.updateVariablePopupPosition}
            templateType={templateType}
            allowConditionalVariables={allowConditionalVariables}
          >
            <div
              ref={this.variablePopupContainer}
              className={styles.mention}
              style={{
                top: Math.round(variablePopupPosition.top),
                left: Math.round(variablePopupPosition.left),
                position: 'fixed',
              }}
            />
          </CKVariablesPopup>
        ) : null}

        <div
          data-cy={testId}
          ref={this.wrapperRef}
          className={customClassName}
          id={id}
        >
          <CKEditor5
            data={data}
            placeholder={placeholder}
            config={config}
            readonly={!!readonly}
            onInit={onInit}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onError={onError}
          />
        </div>
      </ErrorBoundary>
    );
  }
}
